import { userDisplayName } from "@cp/toolkit";
import { useAccount } from "@qw/auth";
import { isInternal } from "@qw/qw-common";
import { Badge, cn, GridCell, GridRow, isPreviewable } from "@qw/ui";
import { format } from "date-fns";
import type { FC } from "react";
import type { InsuredFile } from "../../hooks";
import { displayTypeParts, fileDisplayType, labelsBesideChip } from "../../util";
import { FileMenu } from "./file-menu";

export interface FileRowProps {
  file: InsuredFile;
  fileLabels: boolean;
  openFile: (file: InsuredFile) => void;
}

export const FileRow: FC<FileRowProps> = ({ file, fileLabels, openFile }) => {
  const { user } = useAccount();
  const canPreview = isPreviewable(file.mimeType);
  const hasFileActions = isInternal(user);
  const uploadedBy = userDisplayName(file.uploader);
  const fileDate = format(new Date(file.deletedAt ?? file.createdAt), "M/d/yy");
  const displayType = fileDisplayType(file);
  const labels = fileLabels ? labelsBesideChip(file.labels, displayType) : [];
  const typeParts = displayType ? displayTypeParts(displayType) : null;
  const typeChip = typeParts && (
    <span className="inline-flex items-center gap-1">
      <Badge variant="secondary" type="square" size="2xs">
        {typeParts.type}
      </Badge>
      {typeParts.state && (
        <Badge variant="outline" type="square" size="2xs">
          {typeParts.state}
        </Badge>
      )}
    </span>
  );

  return (
    <GridRow className={cn("py-3 @max-3xl/files:items-start", file?.deletedAt && "text-destructive")}>
      {hasFileActions && (
        <div className="flex @max-3xl/files:col-start-1 @max-3xl/files:row-start-1 @max-3xl/files:justify-start @3xl/files:hidden">
          <FileMenu file={file} />
        </div>
      )}
      <GridCell className="@max-3xl/files:hidden">{typeChip}</GridCell>
      <GridCell
        className={cn(
          "@max-3xl/files:min-w-0 @max-3xl/files:whitespace-normal @3xl/files:col-start-2 @3xl/files:row-start-auto",
          hasFileActions
            ? "@max-3xl/files:col-start-1 @max-3xl/files:row-start-2 @[375px]/files:col-start-2 @[375px]/files:row-start-1"
            : "@max-3xl/files:col-span-full @max-3xl/files:row-start-1"
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          {typeChip && <span className="@3xl/files:hidden flex-none">{typeChip}</span>}
          {canPreview ? (
            <button
              type="button"
              className="block min-w-0 max-w-full truncate text-left hover:underline cursor-pointer"
              onClick={() => openFile(file)}
            >
              {file?.filename}
            </button>
          ) : (
            <div className="min-w-0 truncate">{file?.filename}</div>
          )}
        </div>
        {labels[0] && (
          <div className="@max-3xl/files:hidden flex flex-wrap gap-1 mt-2">
            {labels.map((tag) => (
              <Badge key={tag.label} variant="secondary" type="square" size="2xs">
                {tag.label}
              </Badge>
            ))}
          </div>
        )}
        <div className="@3xl/files:hidden mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-2xs text-muted-foreground">
          {uploadedBy && <span className="min-w-0 max-w-full truncate">{uploadedBy}</span>}
          <span>{fileDate}</span>
          {labels.map((tag) => (
            <Badge key={tag.label} variant="secondary" type="square" size="2xs">
              {tag.label}
            </Badge>
          ))}
        </div>
      </GridCell>
      <GridCell className="@max-3xl/files:hidden">{uploadedBy}</GridCell>
      <GridCell className="@max-3xl/files:hidden">{fileDate}</GridCell>
      {hasFileActions && (
        <div className="hidden @3xl/files:col-start-5 @3xl/files:row-start-auto @3xl/files:flex">
          <FileMenu file={file} />
        </div>
      )}
    </GridRow>
  );
};
